import { useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import classNames from "classnames";

import { CCard, CCardBody, CCardHeader } from "@coreui/react";

import { dinhMucTonKhoApi } from "src/1/apis/dinh_muc_ton_kho.api";
import { CTable } from "src/1/common/components/others";
import { warningDelete } from "src/App";
import { CCheckbox } from "src/common/components/controls";
import { useMobileDevice } from "src/utils/hooks/viewport";
import {
  QUERY_PARAMS_KEYS,
  useSavedQueryParams,
} from "src/zustand/saved-query-params";

import { DinhMucTonKhoModal, DinhMucTonKhoToolbar } from "../../components";

export default function DanhSachDinhMucTonKhoPage() {
  //#region Data
  const { params, setQuery } = useSavedQueryParams((state) => ({
    params: state.params?.[QUERY_PARAMS_KEYS.DANH_SACH_DINH_MUC_TON_KHO]
      ?.query || {
      page: 1,
      size: 10,
      nvlId: "",
      name: "",
      storeCode: "",
      groupCode: "",
    },
    setQuery: state.setQuery,
  }));

  const isMobile = useMobileDevice();

  const [selectedId, setSelectedId] = useState("");

  const { data, isFetching, refetch } = useQuery({
    queryKey: ["danh-sach-dinh-muc-ton-kho", params],
    queryFn: () => dinhMucTonKhoApi.getPaginate(params),
    select: (response) => response?.data,
  });

  const listData = useMemo(() => data?.data ?? [], [data]);

  const modalRef = useRef(null);
  //#endregion

  //#region Event
  const onPageChange = (newPage) =>
    setQuery(QUERY_PARAMS_KEYS.DANH_SACH_DINH_MUC_TON_KHO, {
      ...params,
      page: newPage,
    });

  const onLimitChange = (newLimit) =>
    setQuery(QUERY_PARAMS_KEYS.DANH_SACH_DINH_MUC_TON_KHO, {
      ...params,
      size: newLimit,
      page: 1,
    });

  const onSearch = (searchValues) =>
    setQuery(QUERY_PARAMS_KEYS.DANH_SACH_DINH_MUC_TON_KHO, {
      ...searchValues,
      size: params?.size || 10,
      page: 1,
    });

  const onAdd = () => modalRef.current?.open();

  const onEdit = () => {
    const selectedRecord = listData.find((item) => item.id === selectedId);
    modalRef.current?.open(selectedId, selectedRecord);
  };

  const onRemove = () => {
    warningDelete({
      onConfirm: () => dinhMucTonKhoApi.remove(selectedId),
      onSuccess: () => {
        refetch();
        return "Xóa định mức tồn kho thành công!";
      },
      onError: (error) =>
        error?.message || "Xóa định mức tồn kho không thành công!",
    });
  };

  const onCheck = (id) => (checked) => {
    if (checked) {
      setSelectedId(id);
    } else {
      setSelectedId("");
    }
  };
  //#endregion

  //#region Render
  const headers = [
    {
      key: "selection",
      label: "",
      width: 60,
      cellRender: (value, record) => (
        <CCheckbox
          value={record.id === selectedId}
          onChange={onCheck(record.id)}
        />
      ),
    },
    {
      key: "nvl_id",
      label: "Mã NVL",
      width: 180,
    },
    {
      key: "name",
      label: "Tên NVL",
      width: 270,
      align: "left",
    },
    {
      key: "ware_unit",
      label: "Đơn vị tính",
      width: 130,
    },
    {
      key: "from",
      label: "Từ Thứ",
      width: 140,
    },
    {
      key: "to",
      label: "Đến Thứ",
      width: 140,
    },
    {
      key: "normal",
      label: "Ngày Thường",
      width: 180,
    },
    {
      key: "holiday",
      label: "Ngày Lễ",
      width: 180,
    },
    {
      key: "store_name",
      label: "Cửa Hàng Áp Dụng",
      width: 240,
      align: "left",
    },
  ];
  return (
    <>
      <CCard className={classNames("toolbar", !isMobile && "sticky")}>
        <CCardBody>
          <DinhMucTonKhoToolbar
            params={params}
            onEdit={onEdit}
            onAdd={onAdd}
            onSearch={onSearch}
            onRemove={onRemove}
            canEdit={!!selectedId}
            canRemove={!!selectedId}
          />
        </CCardBody>
      </CCard>
      <CCard>
        <CCardHeader></CCardHeader>
        <CCardBody className="px-0 pt-0">
          <CTable
            loading={isFetching}
            headers={headers}
            data={listData}
            divider
            height={450}
            page={params?.page}
            pages={data?.metadata?.last_page}
            limit={params?.size}
            total={data?.metadata?.total}
            onPageChange={onPageChange}
            onLimitChange={onLimitChange}
          />
        </CCardBody>
      </CCard>

      <DinhMucTonKhoModal ref={modalRef} refetch={refetch} />
    </>
  );
  //#endregion
}
